import React, { useState, useRef } from 'react';
import { getCookie } from '../utils';

export default function Profile({ defaultUser, userRole, userUid }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
  // Create a ref for the hidden file input
  const fileInputRef = useRef(null);

  const apiUrl = userRole === 'admin'
    ? 'https://tsaapis.operate.live/api/admin'
    : 'https://tsaapis.operate.live/api/students';

  let formattedDOB = '';
  if (userRole === 'student' && defaultUser.dob) {
    formattedDOB = new Date(defaultUser.dob).toISOString().split('T')[0];
  }
  const profile = { ...defaultUser, formattedDOB };

  // Maintain the profile picture URL in state so it updates immediately after upload
  const safeName = profile.name ? profile.name.replace(' ', '+') : 'User';
  const defaultAvatar = `https://ui-avatars.com/api/?name=${safeName}&background=fbbf24&color=0f172a&size=150`;
  const [picUrl, setPicUrl] = useState(profile.picUrl || profile.profilePic || defaultAvatar);

  // --- 1. HANDLE IMAGE UPLOAD ---
  const handleImageClick = () => {
    // Trigger the hidden file input when the image is clicked
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate if profile ID exists (needed for naming)
    if (!userUid) {
      alert("Profile ID not found. Please save your profile first before uploading a picture.");
      return;
    }

    const newFileName = `${userRole}_${userUid}`;
    
    // Optional: Extract file extension to append to the name (e.g., .jpg, .png)
    // const fileExt = file.name.split('.').pop();
    // const fullFileName = `${newFileName}.${fileExt}`;

    const formData = new FormData();
    formData.append('image', file);
    formData.append('fileName', newFileName);

    setIsUploading(true);
    try {
      const token = getCookie('tsa_token');
      
      // NOTE: You must create an endpoint on your backend (e.g., /upload) 
      // that receives this FormData, uploads it to Google Drive using the Drive API, 
      // and returns the public URL.
      const res = await fetch(`https://tsaapis.operate.live/api/upload`, {
        method: 'POST',
        headers: {
          'x-auth-token': token || ''
          // IMPORTANT: Do NOT set 'Content-Type' manually when sending FormData. 
          // The browser will automatically set it to 'multipart/form-data' with the correct boundary.
        },
        credentials: 'include',
        body: formData
      });

      if (!res.ok) throw new Error("Failed to upload image");
      
      const data = await res.json();
      
      // Assuming your backend returns { publicUrl: "https://drive.google.com/..." }
      if (data.publicUrl) {
        setPicUrl(data.publicUrl);
        alert("Profile picture uploaded successfully!");
      }
      
    } catch (error) {
      console.error("Upload Error:", error);
      alert("Error uploading image. Please check the console.");
    } finally {
      setIsUploading(false);
      // Reset the file input so the same file can be selected again if needed
      e.target.value = null; 
    }
  };

  // --- 2. SAVE PROFILE DATA ---
  const handleSave = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(e.target);

    // Use the picUrl state which contains either the original or newly uploaded URL
    const apiPayload = {
      name: formData.get('name'),
      picUrl: picUrl, 
      email: formData.get('email'),
      mobile: formData.get('mobile')
    };

    if (userRole === 'student') {
      apiPayload.DOB = formData.get('DOB') || null;
      apiPayload.whatsapp = formData.get('whatsapp');
      apiPayload.emergency = formData.get('emergency');
      apiPayload.address = formData.get('address');
      apiPayload.refferedBy = formData.get('refferedBy');
    }

    try {
      const token = getCookie('tsa_token');
      const apiHeaders = {
        'Content-Type': 'application/json',
        'x-auth-token': token || ''
      };

      if (profile && userUid) {
        const res = await fetch(`${apiUrl}/${userUid}`, {
          method: 'PUT',
          headers: apiHeaders,
          credentials: 'include',
          body: JSON.stringify(apiPayload)
        });

        if (!res.ok) throw new Error("Failed to update profile");
        alert("Profile updated successfully!");
      } 
    } catch (error) {
      console.error("API Error:", error);
      alert("Error saving profile. Please check the console.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayData = profile;

  return (
    <div className="admin-card">
      <h2 style={{ marginBottom: '2rem' }}>My Profile</h2>

      <div className="profile-grid">
        {/* Left Col: Avatar & Role */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.2)', padding: '2rem', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)', height: 'fit-content' }}>
          
          {/* Hidden File Input */}
          <input 
            type="file" 
            ref={fileInputRef} 
            style={{ display: 'none' }} 
            accept="image/*"
            onChange={handleFileChange}
          />
          
          {/* Clickable Profile Image Container */}
          <div 
            style={{ position: 'relative', cursor: isUploading ? 'wait' : 'pointer' }}
            onClick={!isUploading ? handleImageClick : undefined}
            title="Click to change profile picture"
          >
            <img
              src={picUrl}
              alt="Profile"
              style={{ 
                width: '120px', 
                height: '120px', 
                borderRadius: '50%', 
                objectFit: 'cover', 
                border: '4px solid var(--accent-gold)', 
                marginBottom: '1rem',
                opacity: isUploading ? 0.5 : 1,
                transition: 'opacity 0.3s'
              }}
            />
            {/* Optional Loading Overlay */}
            {isUploading && (
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: 'white', textShadow: '0px 0px 4px #000' }}>
                <i className="fas fa-spinner fa-spin fa-2x"></i>
              </div>
            )}
          </div>
          
          <h3 style={{ margin: '0 0 5px 0', color: 'white', textAlign: 'center' }}>
            {displayData.name || 'Update Name'}
          </h3>

          {userRole === 'student' && displayData.regId && (
            <p style={{ margin: '5px 0 15px 0', color: 'var(--accent-blue)', fontFamily: 'monospace', fontSize: '1rem' }}>
              {displayData.regId}
            </p>
          )}

          <span className="badge" style={{ margin: 0 }}>
            {userRole === 'admin' ? 'Super Admin' : 'Active Member'}
          </span>
        </div>

        {/* Right Col: Edit Form */}
        <div style={{ maxHeight: 'calc(100vh - 320px)', overflowY: 'auto', paddingRight: '15px', paddingBottom: '20px' }}>
          <form onSubmit={handleSave} style={{ overflow: 'visible' }}>
            <div className="form-grid" style={{ overflow: 'visible' }}>

              {/* Shared Fields */}
              <div className="form-group">
                <label>Full Name</label>
                <input type="text" name="name" defaultValue={displayData.name} required readOnly />
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <input type="email" name="email" defaultValue={displayData.email} required readOnly />
              </div>

              <div className="form-group">
                <label>Mobile Number</label>
                <input type="tel" name="mobile" defaultValue={displayData.mobile || displayData.phone} required disabled={isSubmitting} />
              </div>

              {/* Student Fields */}
              {userRole === 'student' && (
                <>
                  <div className="form-group">
                    <label>WhatsApp Number</label>
                    <input type="tel" name="whatsapp" defaultValue={displayData.whatsapp} placeholder="Optional" disabled={isSubmitting} />
                  </div>
                  <div className="form-group">
                    <label>Date of Birth</label>
                    <input type="date" name="DOB" defaultValue={displayData.formattedDOB} disabled={isSubmitting} />
                  </div>
                  <div className="form-group">
                    <label>Emergency Contact</label>
                    <input type="tel" name="emergency" defaultValue={displayData.emergency} placeholder="Name & Phone" disabled={isSubmitting} />
                  </div>
                  <div className="form-group full-width">
                    <label>Residential Address</label>
                    <input type="text" name="address" defaultValue={displayData.address} placeholder="Full Address" disabled={isSubmitting} />
                  </div>
                  <div className="form-group full-width">
                    <label>Referred By</label>
                    <input type="text" name="refferedBy" defaultValue={displayData.refferedBy} placeholder="Who recommended you to us?" readOnly />
                  </div>
                </>
              )}

            </div>

            <div className="form-actions" style={{ justifyContent: 'flex-end', marginTop: '25px', paddingRight: '5px' }}>
              <button type="submit" className="submit-btn" disabled={isSubmitting || isUploading}>
                <i className={isSubmitting ? "fas fa-spinner fa-spin" : "fas fa-save"}></i>
                {isSubmitting ? ' Saving...' : ' Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}